import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../services/api_config';
import { getPartnerTheme } from '../../config/partnerThemes';

// Emit setup. Only the name is required; every other answer is optional and helps the
// coach speak to the person's life by name. Same family shape as SAFE's setup.
const EMPTY_FAMILY = { relationship: '', partner_role: '', partner_name: '', children: [], grandchildren: [], other: '' };

const DENOMINATIONS = [
  { value: '', label: 'Select your tradition (optional)...' },
  { value: 'non_denominational', label: 'Non-Denominational' },
  { value: 'baptist', label: 'Baptist' },
  { value: 'catholic', label: 'Catholic' },
  { value: 'methodist', label: 'Methodist' },
  { value: 'lutheran', label: 'Lutheran' },
  { value: 'presbyterian', label: 'Presbyterian' },
  { value: 'pentecostal', label: 'Pentecostal' },
  { value: 'episcopal', label: 'Episcopal / Anglican' },
  { value: 'orthodox', label: 'Orthodox' },
  { value: 'church_of_christ', label: 'Church of Christ' },
  { value: 'assembly_of_god', label: 'Assembly of God' },
  { value: 'seventh_day_adventist', label: 'Seventh-day Adventist' },
  { value: 'lds', label: 'Latter-day Saints (LDS)' },
  { value: 'jehovah_witness', label: "Jehovah's Witness" },
  { value: 'charismatic', label: 'Charismatic' },
  { value: 'evangelical', label: 'Evangelical' },
  { value: 'reformed', label: 'Reformed' },
  { value: 'quaker', label: 'Quaker' },
  { value: 'mennonite', label: 'Mennonite' },
  { value: 'ame', label: 'AME (African Methodist Episcopal)' },
  { value: 'cogic', label: 'COGIC (Church of God in Christ)' },
  { value: 'other', label: 'Other' },
  { value: 'exploring', label: 'Still Exploring' }
];
const CARE = [
  { value: 'heavy_heart', label: 'Something heavy on my heart' },
  { value: 'stress', label: 'Stress or pressure' },
  { value: 'family', label: 'Family and relationships' },
  { value: 'grief', label: 'Grief and loss' },
  { value: 'faith_questions', label: 'Questions about faith' },
  { value: 'grow_faith', label: 'Growing in my faith' },
  { value: 'health', label: 'Health' },
  { value: 'talk', label: 'I just need to talk' }
];
const WELLBEING = [
  { value: 'doing_well', label: "I'm doing well overall" },
  { value: 'some_stress', label: 'Some stress, but managing' },
  { value: 'struggling', label: 'Struggling most days' },
  { value: 'need_support', label: 'I really need support right now' }
];
const SUPPORT = [
  { value: 'listening', label: 'Someone to listen' },
  { value: 'prayer', label: 'Prayer' },
  { value: 'scripture', label: 'Scripture' },
  { value: 'practical', label: 'Practical next steps' }
];
const STAGES = [
  { value: 'young_adult', label: 'Young adult (18-29)' },
  { value: 'career_family', label: 'Building career and family (30-44)' },
  { value: 'midlife', label: 'Midlife (45-60)' },
  { value: 'senior', label: 'Senior (60+)' },
  { value: 'caregiver', label: 'Caring for someone' }
];
const RELATIONSHIP = [
  { value: 'married', label: 'Married' }, { value: 'relationship', label: 'In a relationship' },
  { value: 'single', label: 'Single' }, { value: 'separated', label: 'Separated or divorced' },
  { value: 'widowed', label: 'Widowed' }
];
const SITUATION = [
  { value: 'working', label: 'Working' }, { value: 'school', label: 'In school or training' },
  { value: 'job_seeking', label: 'Looking for work' }, { value: 'own_business', label: 'Running my own business' },
  { value: 'caregiving', label: 'Caring for family' }, { value: 'retired', label: 'Retired' },
  { value: 'not_working', label: 'Not working right now' }
];
const TOWARD = [
  { value: 'work', label: 'Steady work or a new career' }, { value: 'school', label: 'Finishing school or training' },
  { value: 'build', label: 'Building something of my own' }, { value: 'family', label: 'Being there for my family' },
  { value: 'health', label: 'Getting healthier' }, { value: 'people', label: 'Reconnecting with people' },
  { value: 'purpose', label: 'A sense of purpose' }, { value: 'getting_through', label: 'Getting through this stretch' },
  { value: 'unsure', label: 'Not sure yet' }
];
const MILITARY = [
  { value: 'none', label: 'No' }, { value: 'active', label: 'Active duty' },
  { value: 'veteran', label: 'Veteran' }, { value: 'military_family', label: 'Military family' }
];
const CHURCH = [
  { value: 'yes', label: 'Yes, I have one' }, { value: 'looking', label: "I'm looking for one" },
  { value: 'no', label: 'Not right now' }
];
const STEPS = ['Your Name', 'What Brings You Here', 'How You Like Support', 'Life Stage & Family', 'Your Days & Work', 'Faith & Spirit'];

const EmitProfileSetup = () => {
  const navigate = useNavigate();
  const theme = getPartnerTheme();
  const accent = (theme.partnerName && theme.colors && theme.colors.accent) || '#8b93f0';
  const [step, setStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState({
    preferred_name: '', denomination: '', care_focus: [], wellbeing: '', support_style: [],
    life_stage: '', family: { ...EMPTY_FAMILY }, occupation: '', current_situation: '',
    working_toward: [], military_status: '', church_home: '', coach_preference: ''
  });

  useEffect(() => {
    API.get('/api/v1/profile/emit-profile').then(res => {
      const d = res.data || {};
      if (d.exists) {
        setIsEditing(true);
        setProfile(p => ({
          ...p,
          preferred_name: d.preferred_name || '', denomination: d.denomination || '',
          care_focus: d.care_focus || [], wellbeing: d.wellbeing || '', support_style: d.support_style || [],
          life_stage: d.life_stage || '', family: { ...EMPTY_FAMILY, ...(d.family || {}) },
          occupation: d.occupation || '', current_situation: d.current_situation || '',
          working_toward: d.working_toward || [], military_status: d.military_status || '', church_home: d.church_home || '',
          coach_preference: d.coach_preference || ''
        }));
      }
    }).catch(() => {});
  }, []);

  // Each step opens at the top of the page.
  useEffect(() => {
    try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch (e) { window.scrollTo(0, 0); }
  }, [step]);

  const set = (patch) => setProfile(p => ({ ...p, ...patch }));
  const toggle = (key, value, max) => setProfile(p => {
    const cur = p[key] || [];
    if (cur.includes(value)) return { ...p, [key]: cur.filter(v => v !== value) };
    if (max && cur.length >= max) return p;
    return { ...p, [key]: [...cur, value] };
  });
  const setFamily = (patch) => setProfile(p => ({ ...p, family: { ...(p.family || EMPTY_FAMILY), ...patch } }));
  const rows = (key) => ((profile.family || {})[key] || []);
  const setRow = (key, i, patch) => setFamily({ [key]: rows(key).map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const addRow = (key) => setFamily({ [key]: [...rows(key), { name: '', age: '' }] });
  const removeRow = (key, i) => setFamily({ [key]: rows(key).filter((_, j) => j !== i) });

  const handleSubmit = async () => {
    setIsLoading(true);
    setError('');
    try {
      await API.post('/api/v1/profile/emit-profile', profile);
      navigate('/emit-the-bible');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setIsLoading(false);
    }
  };

  const nameOk = profile.preferred_name.trim().length > 0;
  const last = step === STEPS.length - 1;

  // ---- styles (text never below 14px) ----
  const h2 = { fontSize: '22px', fontWeight: 700, margin: '0 0 6px 0', color: 'white', lineHeight: 1.3 };
  const hint = { color: 'rgba(255,255,255,0.65)', fontSize: '15px', margin: '0 0 16px 0', lineHeight: 1.45 };
  const label = { display: 'block', color: 'rgba(255,255,255,0.88)', fontSize: '16px', fontWeight: 600, margin: '24px 0 10px' };
  const input = { width: '100%', padding: '14px 16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.06)', color: 'white', fontSize: '16px', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit', minWidth: 0 };
  const chipRow = { display: 'flex', flexWrap: 'wrap', gap: '10px' };
  const chip = (on) => ({ padding: '11px 16px', borderRadius: '22px', cursor: 'pointer', fontSize: '15px', fontFamily: 'inherit', color: 'white', background: on ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.06)', border: on ? `2px solid ${accent}` : '1px solid rgba(255,255,255,0.25)', fontWeight: on ? 700 : 500 });
  const card = (on) => ({ ...chip(on), borderRadius: '12px', width: '100%', textAlign: 'left', padding: '14px 16px' });
  // Plain render helpers, not components: a component defined inside this one would be
  // remounted on every keystroke and the name boxes would lose focus.
  const chips = (options, value, onPick) => (
    <div style={chipRow}>
      {options.map(o => {
        const on = Array.isArray(value) ? value.includes(o.value) : value === o.value;
        return <button key={o.value} type="button" aria-pressed={on} onClick={() => onPick(o.value)} style={chip(on)}>{on ? '\u2713 ' : ''}{o.label}</button>;
      })}
    </div>
  );
  const peopleRows = (k, noun) => (
    <>
      {rows(k).map((r, i) => (
        <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <input type="text" value={r.name || ''} maxLength={30} placeholder="First name (optional)" aria-label={noun + ' ' + (i + 1) + ' name'}
            onChange={e => setRow(k, i, { name: e.target.value })} style={{ ...input, flex: 1 }} />
          <input type="number" inputMode="numeric" min="0" max="120" value={r.age ?? ''} placeholder="Age" aria-label={noun + ' ' + (i + 1) + ' age'}
            onChange={e => setRow(k, i, { age: e.target.value })} style={{ ...input, width: '84px', flex: '0 0 84px' }} />
          <button type="button" onClick={() => removeRow(k, i)} aria-label={'Remove ' + noun + ' ' + (i + 1)}
            style={{ width: '46px', flex: '0 0 46px', background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '12px', color: 'rgba(255,255,255,0.85)', fontSize: '20px', cursor: 'pointer' }}>{'\u00D7'}</button>
        </div>
      ))}
      <button type="button" onClick={() => addRow(k)}
        style={{ padding: '10px 14px', background: 'transparent', border: '1px dashed rgba(255,255,255,0.4)', borderRadius: '12px', color: 'white', fontSize: '15px', cursor: 'pointer', fontFamily: 'inherit' }}>+ Add a {noun.toLowerCase()}</button>
    </>
  );

  const fam = profile.family || EMPTY_FAMILY;
  // The Faith / Spiritual choice: standard Emit and the Chaplain app. Branded Christian partners stay Faith only.
  const showCoachChoice = !theme.partnerName || theme.id === 'chaplain' || theme.id === 'faith';
  const spiritual = showCoachChoice && profile.coach_preference === 'sage';
  const labels = theme.coachLabels || { emit: 'Emit, Christian-based', sage: 'SAGE, spiritual-based' };
  const COACHES = [
    { value: 'emit', label: labels.emit, sub: 'Christian. Scripture and prayer woven into the conversation.' },
    { value: 'sage', label: labels.sage, sub: 'Spiritual, not tied to one religion. Speaks to your spirit in your own words.' }
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#1a1a2e', padding: '20px 16px', color: 'white', display: 'flex', justifyContent: 'center' }}>
      <div style={{ maxWidth: '560px', width: '100%', background: '#252547', borderRadius: '20px', padding: '32px 24px', boxShadow: '0 10px 40px rgba(0,0,0,0.3)', alignSelf: 'flex-start', boxSizing: 'border-box' }}>
        <div style={{ textAlign: 'center', marginBottom: '22px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', margin: '0 0 8px 0' }}>
            {isEditing ? 'Edit Your Profile' : (theme.partnerName ? 'Welcome to ' + theme.partnerName : 'Welcome to Emit')}
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.7)', margin: '0 0 14px 0', fontSize: '15px' }}>
            {STEPS[step]} {'\u2014'} Step {step + 1} of {STEPS.length}
          </p>
          <div style={{ display: 'flex', gap: '6px' }}>
            {STEPS.map((_, i) => (
              <div key={i} style={{ flex: 1, height: '4px', borderRadius: '2px', background: i <= step ? accent : 'rgba(255,255,255,0.2)' }} />
            ))}
          </div>
        </div>

        {step === 0 && (
          <div>
            <h2 style={h2}>What should we call you?</h2>
            <p style={hint}>Use whatever name feels right.</p>
            <input type="text" placeholder="Your name or nickname" value={profile.preferred_name} maxLength={40}
              onChange={e => set({ preferred_name: e.target.value })} style={input} />
          </div>
        )}

        {step === 1 && (
          <div>
            <h2 style={h2}>What brings you here?</h2>
            <p style={hint}>Pick up to two. (Optional)</p>
            {chips(CARE, profile.care_focus, v => toggle('care_focus', v, 2))}
            <label style={label}>How are you doing right now?</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {WELLBEING.map(o => (
                <button key={o.value} type="button" aria-pressed={profile.wellbeing === o.value}
                  onClick={() => set({ wellbeing: profile.wellbeing === o.value ? '' : o.value })} style={card(profile.wellbeing === o.value)}>
                  {profile.wellbeing === o.value ? '\u2713 ' : ''}{o.label}
                </button>
              ))}
            </div>
            {profile.wellbeing === 'need_support' && (
              <p style={{ ...hint, marginTop: '14px', color: 'white' }}>If you are in danger or thinking about harming yourself, call or text 988 now, or call 911.</p>
            )}
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 style={h2}>How do you like to be supported?</h2>
            <p style={hint}>Pick any that fit. (Optional)</p>
            {chips(SUPPORT, profile.support_style, v => toggle('support_style', v))}
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 style={h2}>Your life stage</h2>
            <p style={hint}>(Optional)</p>
            {chips(STAGES, profile.life_stage, v => set({ life_stage: profile.life_stage === v ? '' : v }))}
            <h2 style={{ ...h2, marginTop: '32px' }}>Your family</h2>
            <p style={hint}>Optional. It lets your coach talk with you about the people in your life by name. Share only what you want to.</p>
            <label style={label}>Relationship</label>
            {chips(RELATIONSHIP, fam.relationship, v => setFamily({ relationship: fam.relationship === v ? '' : v }))}
            {['married', 'relationship'].includes(fam.relationship) && (
              <>
                <label style={label}>Your spouse or partner</label>
                <div style={{ marginBottom: '10px' }}>
                  {chips([{ value: 'husband', label: 'Husband' }, { value: 'wife', label: 'Wife' }, { value: 'partner', label: 'Partner' }], fam.partner_role, v => setFamily({ partner_role: fam.partner_role === v ? '' : v }))}
                </div>
                <input type="text" value={fam.partner_name || ''} maxLength={30} placeholder="First name (optional)" aria-label="Spouse or partner's first name"
                  onChange={e => setFamily({ partner_name: e.target.value })} style={input} />
              </>
            )}
            <label style={label}>Children</label>
            {peopleRows('children', 'Child')}
            <label style={label}>Grandchildren</label>
            {peopleRows('grandchildren', 'Grandchild')}
            <label style={label}>Anyone else at home or close to you?</label>
            <input type="text" value={fam.other || ''} maxLength={120} placeholder="For example: my mom lives with us"
              onChange={e => setFamily({ other: e.target.value })} style={input} />
          </div>
        )}

        {step === 4 && (
          <div>
            <h2 style={h2}>What takes up most of your days?</h2>
            <p style={hint}>Pick one. (Optional)</p>
            {chips(SITUATION, profile.current_situation, v => set({ current_situation: profile.current_situation === v ? '' : v }))}
            <label style={label}>What do you do for work?</label>
            <input type="text" value={profile.occupation} maxLength={80} placeholder="For example: nurse, truck driver, teacher, student"
              onChange={e => set({ occupation: e.target.value })} style={input} />
            <label style={label}>What are you working toward? (up to two)</label>
            {chips(TOWARD, profile.working_toward, v => toggle('working_toward', v, 2))}
            <label style={label}>Do you have a military connection?</label>
            {chips(MILITARY, profile.military_status, v => set({ military_status: profile.military_status === v ? '' : v }))}
          </div>
        )}

        {step === 5 && (
          <div>
            {showCoachChoice && (
              <>
                <h2 style={h2}>How would you like your coach to speak with you?</h2>
                <p style={hint}>You can change this any time in Edit Profile.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {COACHES.map(o => {
                    const on = (profile.coach_preference || 'emit') === o.value;
                    return (
                      <button key={o.value} type="button" aria-pressed={on} onClick={() => set({ coach_preference: o.value })} style={card(on)}>
                        <span style={{ display: 'block', fontSize: '17px', fontWeight: 700 }}>{on ? '\u2713 ' : ''}{o.label}</span>
                        <span style={{ display: 'block', fontSize: '15px', fontWeight: 400, color: 'rgba(255,255,255,0.78)', marginTop: '4px', lineHeight: 1.4 }}>{o.sub}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            {!spiritual && (
              <>
                <label style={{ ...label, marginTop: showCoachChoice ? '28px' : '8px' }}>Your faith tradition (optional)</label>
                <select value={profile.denomination} onChange={e => set({ denomination: e.target.value })}
                  style={{ ...input, background: '#1a1a2e', cursor: 'pointer' }}>
                  {DENOMINATIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </>
            )}
            <label style={label}>{spiritual ? 'Do you have a spiritual community?' : 'Do you have a church home?'}</label>
            {chips(CHURCH, profile.church_home, v => set({ church_home: profile.church_home === v ? '' : v }))}
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
          {step > 0 && (
            <button type="button" onClick={() => setStep(step - 1)}
              style={{ flex: 1, padding: '16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.3)', background: 'transparent', color: 'white', fontSize: '16px', cursor: 'pointer', fontFamily: 'inherit' }}>
              Back
            </button>
          )}
          <button type="button" disabled={!nameOk || isLoading}
            onClick={() => (last ? handleSubmit() : setStep(step + 1))}
            style={{ flex: 2, padding: '16px', borderRadius: '12px', border: 'none', fontFamily: 'inherit',
              background: nameOk ? (theme.partnerName ? accent : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)') : 'rgba(255,255,255,0.1)',
              color: nameOk ? (theme.partnerName ? '#1a1a2e' : 'white') : 'rgba(255,255,255,0.5)',
              fontSize: '17px', fontWeight: 700, cursor: nameOk && !isLoading ? 'pointer' : 'not-allowed', opacity: isLoading ? 0.7 : 1 }}>
            {isLoading ? 'Saving...' : (last ? (isEditing ? 'Save Changes' : 'Begin') : 'Continue')}
          </button>
        </div>

        {error && (
          <div style={{ marginTop: '15px', textAlign: 'center', color: '#fca5a5', fontSize: '15px' }}>{error}</div>
        )}

        {isEditing && (
          <button type="button" onClick={() => navigate('/emit-the-bible')}
            style={{ width: '100%', marginTop: '12px', padding: '12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.2)', background: 'transparent', color: 'rgba(255,255,255,0.75)', fontSize: '15px', cursor: 'pointer', fontFamily: 'inherit' }}>
            Cancel and Return
          </button>
        )}
      </div>
    </div>
  );
};

export default EmitProfileSetup;
